import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";

const megaMenus = {
  Services: {
    featured: { label: "All Services", desc: "See everything we offer", to: "/services" },
    cols: [
      {
        heading: "Growth & Visibility",
        items: [
          { icon: "⌁", label: "SEO", sub: "Rank higher, get found", to: "/services/seo" },
          { icon: "G", label: "Google Ads & PPC", sub: "High-intent performance campaigns", to: "/services/ppc" },
          { icon: "AI", label: "AI & Automation", sub: "Smarter marketing, less effort", to: "/services/ai-automation" },
        ]
      },
      {
        heading: "Creative & Social",
        items: [
          { icon: "◎", label: "Social Media Marketing", sub: "Build community & demand", to: "/services/social-media" },
          { icon: "✦", label: "Content & Creative", sub: "Campaigns that stop the scroll", to: "/services/content" },
          { icon: "✉", label: "Email & WhatsApp", sub: "Retention & lifecycle journeys", to: "/services/email-whatsapp" },
        ]
      },
      {
        heading: "Build & Brand",
        items: [
          { icon: "</>", label: "Web Design & Dev", sub: "Fast, beautiful, high-converting", to: "/services/web-development" },
          { icon: "◉", label: "Branding & Design", sub: "Identity & visual systems", to: "/services/branding" },
        ]
      },
    ]
  },
  Industries: {
    featured: { label: "All Industries", desc: "See every vertical we serve", to: "/industries" },
    cols: [
      {
        heading: "By Sector",
        items: [
          { icon: "🛍", label: "E-commerce", sub: "Acquisition, CRO & retention", to: "/industries/ecommerce" },
          { icon: "🏥", label: "Healthcare", sub: "Patient acquisition & trust", to: "/industries/healthcare" },
          { icon: "🏗", label: "Real Estate", sub: "Lead gen & property campaigns", to: "/industries/real-estate" },
        ]
      },
      {
        heading: "",
        items: [
          { icon: "🎓", label: "Education", sub: "Admissions & awareness", to: "/industries/education" },
          { icon: "🍽", label: "Hospitality", sub: "Bookings & local search", to: "/industries/hospitality" },
          { icon: "💼", label: "Professional Services", sub: "Authority & lead generation", to: "/industries/professional" },
        ]
      },
    ]
  },
  Company: {
    featured: { label: "About Us", desc: "Our story, team & values", to: "/about" },
    cols: [
      {
        heading: "Who We Are",
        items: [
          { icon: "👥", label: "About", sub: "Our story & approach", to: "/about" },
          { icon: "⚙", label: "Our Process", sub: "How we work", to: "/process" },
          { icon: "💼", label: "Our Work", sub: "Case studies & results", to: "/work" },
        ]
      },
      {
        heading: "Connect",
        items: [
          { icon: "💰", label: "Pricing", sub: "Transparent investment tiers", to: "/pricing" },
          { icon: "📚", label: "Resources", sub: "Guides, tips & insights", to: "/resources" },
          { icon: "📞", label: "Contact", sub: "Let's start a conversation", to: "/contact" },
        ]
      },
    ]
  }
};

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const timeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); setActiveMenu(null); }, [location]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openMenu = (name) => {
    clearTimeout(timeoutRef.current);
    setActiveMenu(name);
  };
  const closeMenu = () => {
    timeoutRef.current = setTimeout(() => setActiveMenu(null), 120);
  };

  return (
    <>
      <nav className={`nav${scrolled ? " nav-scrolled" : ""}`}>
        <Link to="/" className="nav-logo-link">
          <img className="logo" src={logo} alt="Digital Monks 4 You" />
        </Link>

        <div className={`navlinks${mobileOpen ? " mobile-open" : ""}`}>
          {Object.keys(megaMenus).map(name => (
            <div
              key={name}
              className="nav-item"
              onMouseEnter={() => openMenu(name)}
              onMouseLeave={closeMenu}
            >
              <span className={`nav-label${activeMenu === name ? " active" : ""}`}>
                {name} <span className="nav-chevron">›</span>
              </span>

              {activeMenu === name && (
                <div
                  className="mega-menu"
                  onMouseEnter={() => openMenu(name)}
                  onMouseLeave={closeMenu}
                >
                  <div className="mega-inner">
                    <div className="mega-featured">
                      <Link to={megaMenus[name].featured.to} className="mega-feat-link">
                        <div className="mega-feat-icon">✦</div>
                        <div>
                          <div className="mega-feat-title">{megaMenus[name].featured.label}</div>
                          <div className="mega-feat-sub">{megaMenus[name].featured.desc}</div>
                        </div>
                        <span className="mega-feat-arrow">↗</span>
                      </Link>
                      <div className="mega-badge">Think Digital. Grow Beyond.</div>
                    </div>
                    <div className="mega-cols">
                      {megaMenus[name].cols.map((col, ci) => (
                        <div key={ci} className="mega-col">
                          {col.heading && <div className="mega-col-heading">{col.heading}</div>}
                          {col.items.map(item => (
                            <Link key={item.to} to={item.to} className="mega-item">
                              <span className="mega-item-icon">{item.icon}</span>
                              <div>
                                <div className="mega-item-label">{item.label}</div>
                                <div className="mega-item-sub">{item.sub}</div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          <NavLink to="/contact" className={({ isActive }) => `nav-simple${isActive ? " active" : ""}`}>Contact</NavLink>
        </div>

        <Link className="cta nav-cta" to="/contact">Free Consultation →</Link>
        <button className="menu" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
          {mobileOpen ? "×" : "☰"}
        </button>
      </nav>

      {/* Mobile backdrop */}
      {mobileOpen && <div className="nav-backdrop" onClick={() => setMobileOpen(false)} />}
    </>
  );
}
